import React, { createContext, useContext, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useConversationUuid } from '~/hooks'
import { TaskDetails, useTaskDetails } from '~/hooks/task/useTaskDetails'
import { AgentTaskStatus } from '~/protos/agents'
import { SocketEvent } from '~/services/socket'
import { TaskObject } from '~/types/TaskType'
import { AppEvents, globalEventEmitter } from '~/utils/shared'

interface TaskContextValue {
  currentTask?: TaskDetails
  currentTaskId: string | null
  isLoadingCurrentTask: boolean
  currentTaskError: unknown
  refetch: () => void
}

export const TaskContext = createContext<TaskContextValue | undefined>(undefined)

export function getTaskRequest(task?: TaskObject) {
  if (!task) return undefined
  const orderedStateResponses = [...task.details.orderedStateResponses]
  orderedStateResponses.sort((a, b) => b.createdTsSec - a.createdTsSec)
  const submittedResponse = orderedStateResponses.find(
    response => response.taskStatus === AgentTaskStatus.SUBMITTED
  )
  if (submittedResponse) {
    return submittedResponse.request
  }
  return task.details.originalRequest
}

function TaskHandler({ children }: { children: React.ReactNode }) {
  const taskContext = useTaskContext()

  useEffect(() => {
    const handleTaskUpdated = (...args: unknown[]) => {
      const data = args[0] as {
        event: string
        data: {
          task_id?: number
          status?: string
          cmd_type?: string
          conversation_id?: number
        }
      }

      if (data.event !== SocketEvent.TaskUpdated) return

      try {
        if (data.data?.task_id && taskContext?.currentTaskId) {
          const updatedTaskId = String(data.data.task_id)
          if (updatedTaskId === taskContext.currentTaskId) {
            taskContext.refetch()
          }
        }
      } catch (error) {
        console.error('Error handling task update:', error)
      }
    }

    globalEventEmitter.on(AppEvents.SocketEvent, handleTaskUpdated)
    return () => {
      globalEventEmitter.off(AppEvents.SocketEvent, handleTaskUpdated)
    }
  }, [taskContext])

  return children
}

interface TaskProviderProps {
  children: React.ReactNode
}

export function TaskProvider({ children }: TaskProviderProps) {
  const [searchParams] = useSearchParams()
  const conversationUuid = useConversationUuid()
  const currentTaskId = searchParams.get('task_id')

  // Always fetch current task if there's a task_id in URL
  const {
    data: currentTask,
    isLoading: isLoadingCurrentTask,
    error: currentTaskError,
    refetch
  } = useTaskDetails(conversationUuid, currentTaskId || '')
  const contextValue: TaskContextValue = useMemo(
    () => ({
      currentTask,
      currentTaskId,
      isLoadingCurrentTask,
      currentTaskError,
      refetch: () => {
        if (currentTaskId && conversationUuid) {
          refetch()
        }
      }
    }),
    [currentTask, currentTaskId, isLoadingCurrentTask, currentTaskError, refetch, conversationUuid]
  )

  return (
    <TaskContext.Provider value={contextValue}>
      <TaskHandler>{children}</TaskHandler>
    </TaskContext.Provider>
  )
}

// Hook to use task context
export function useTaskContext(): TaskContextValue {
  const context = useContext(TaskContext)
  if (!context) {
    throw new Error('useTaskContext must be used within a TaskProvider')
  }
  return context
}

// Main useTask hook that prioritizes current context
export function useTask() {
  const { currentTask, currentTaskId, isLoadingCurrentTask, currentTaskError } = useTaskContext()
  return {
    task: currentTask?.task,
    messages: currentTask?.messages ?? [],
    isLoading: isLoadingCurrentTask,
    error: currentTaskError,
    taskId: currentTaskId,
    /** Whether task result is visible (false if executed over credit limit) */
    isResultVisible: currentTask?.is_result_visible ?? true
  }
}
